// Hooks da área de Trackeamento (tag do site, verificação, eventos) e das landing pages.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CreateLandingRequest, CreateLeadSaleRequest, CreateManualLeadRequest, CreateManualLeadResponse, LandingDomainDto, LandingDomainsResponse, LandingLeadsResponse, LeadSalesResponse, LeadSalesSourceSummaryDto, TrackingSourcesDto, LandingListResponse, LandingPageDto, SiteCheckDto, TrackingSetupDto, TrackingStatsDto,
  UpdateLandingRequest, UpdateTrackingRequest,
} from '@impulso/shared/api/tracking'
import type { SubscriptionDto } from '@impulso/shared/api/billing'
import { api } from '../../lib/api'

export const trackingKey = (companyId: string) => ['tracking', companyId] as const
export const landingKey = (companyId: string) => ['landing-pages', companyId] as const

export function useTracking(companyId: string, opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: trackingKey(companyId),
    queryFn: () => api.get<TrackingSetupDto>(`/companies/${companyId}/tracking`),
    // aguardando a instalação: confere sozinho de tempos em tempos
    refetchInterval: (q) => (opts.poll && q.state.data && q.state.data.status !== 'receiving' ? 10_000 : false),
  })
}

export function useUpdateTracking(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateTrackingRequest) => api.patch<TrackingSetupDto>(`/companies/${companyId}/tracking`, body),
    onSuccess: (data) => qc.setQueryData(trackingKey(companyId), data),
  })
}

export function useCheckSite(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (url?: string) => api.post<SiteCheckDto>(`/companies/${companyId}/tracking/check`, url ? { url } : {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackingKey(companyId) }),
  })
}

export function useTrackingStats(companyId: string, days: number) {
  return useQuery({ queryKey: ['tracking', companyId, 'stats', days], queryFn: () => api.get<TrackingStatsDto>(`/companies/${companyId}/tracking/stats?days=${days}`) })
}

export function useSetLandingAddon(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (active: boolean) => api.put<SubscriptionDto>(`/accounts/${accountId}/subscription/addons/landing_pages`, { active }),
    onSuccess: (data) => {
      qc.setQueryData(['subscription', accountId], data)
      void qc.invalidateQueries({ queryKey: ['tracking'] })
      void qc.invalidateQueries({ queryKey: ['landing-pages'] })
    },
  })
}

// ---------------------------------------------------------------- landing pages

export function useLandingPages(companyId: string) {
  return useQuery({ queryKey: landingKey(companyId), queryFn: () => api.get<LandingListResponse>(`/companies/${companyId}/landing-pages`) })
}

export function useLandingPage(companyId: string, id: string) {
  return useQuery({ queryKey: [...landingKey(companyId), id], queryFn: () => api.get<LandingPageDto>(`/companies/${companyId}/landing-pages/${id}`), refetchOnWindowFocus: false })
}

export function useCreateLanding(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateLandingRequest & { whatsapp?: string }) => api.post<LandingPageDto>(`/companies/${companyId}/landing-pages`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: landingKey(companyId) }),
  })
}

export function useUpdateLanding(companyId: string, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateLandingRequest) => api.patch<LandingPageDto>(`/companies/${companyId}/landing-pages/${id}`, body),
    onSuccess: (data) => {
      qc.setQueryData([...landingKey(companyId), id], data)
      void qc.invalidateQueries({ queryKey: landingKey(companyId), exact: true })
    },
  })
}

export function useLandingAction(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'publish' | 'unpublish' | 'duplicate' | 'delete' }) =>
      action === 'delete'
        ? api.del<void>(`/companies/${companyId}/landing-pages/${id}`).then(() => null)
        : api.post<LandingPageDto>(`/companies/${companyId}/landing-pages/${id}/${action}`),
    onSuccess: (data, { id }) => {
      if (data && data.id === id) qc.setQueryData([...landingKey(companyId), id], data)
      void qc.invalidateQueries({ queryKey: landingKey(companyId), exact: true })
      void qc.invalidateQueries({ queryKey: trackingKey(companyId) })
    },
  })
}

export function useLandingLeads(companyId: string, enabled = true) {
  return useQuery({ queryKey: ['landing-leads', companyId], queryFn: () => api.get<LandingLeadsResponse>(`/companies/${companyId}/landing-leads`), enabled })
}

export function useCreateManualLead(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateManualLeadRequest) => api.post<CreateManualLeadResponse>(`/companies/${companyId}/landing-leads`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['landing-leads', companyId] }),
  })
}

export function useLeadSalesSummary(companyId: string, enabled = true) {
  return useQuery({
    queryKey: ['lead-sales-summary', companyId],
    queryFn: () => api.get<LeadSalesSourceSummaryDto>(`/companies/${companyId}/landing-lead-sales/summary`),
    enabled,
  })
}

export function useLeadSales(companyId: string, leadId: string | null) {
  return useQuery({
    queryKey: ['lead-sales', companyId, leadId],
    queryFn: () => api.get<LeadSalesResponse>(`/companies/${companyId}/landing-leads/${leadId}/sales`),
    enabled: !!leadId,
  })
}

export function useCreateLeadSale(companyId: string, leadId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateLeadSaleRequest) => api.post(`/companies/${companyId}/landing-leads/${leadId}/sales`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['lead-sales', companyId, leadId] }),
      qc.invalidateQueries({ queryKey: ['lead-sales-summary', companyId] }),
    ]),
  })
}

export function useCancelLeadSale(companyId: string, leadId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (saleId: string) => api.post<void>(`/companies/${companyId}/landing-leads/${leadId}/sales/${saleId}/cancel`),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['lead-sales', companyId, leadId] }),
      qc.invalidateQueries({ queryKey: ['lead-sales-summary', companyId] }),
    ]),
  })
}

export function useSetLeadStatus(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'new' | 'contacted' }) => api.patch<{ ok: true }>(`/companies/${companyId}/landing-leads/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['landing-leads', companyId] }),
  })
}

export function useDeleteLead(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/companies/${companyId}/landing-leads/${id}`),
    // as vendas do contato somem junto (cascade): o resumo de vendas também muda
    onSuccess: (_, id) => Promise.all([
      qc.invalidateQueries({ queryKey: ['landing-leads', companyId] }),
      qc.invalidateQueries({ queryKey: ['lead-sales-summary', companyId] }),
      qc.invalidateQueries({ queryKey: ['lead-sales', companyId, id] }),
    ]),
  })
}

/** Cópia de todos os dados guardados do contato (LGPD), em arquivo JSON. */
export async function exportLead(companyId: string, id: string): Promise<void> {
  const data = await api.get<unknown>(`/companies/${companyId}/landing-leads/${id}/export`)
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
  a.download = `contato-${id}.json`
  a.click()
  URL.revokeObjectURL(a.href)
}

// ---------------------------------------------------------------- contas conectadas e domínio próprio

/** Consulta as plataformas na hora; só busca quando a tela pede. */
export function useTrackingSources(companyId: string, enabled: boolean) {
  return useQuery({ queryKey: ['tracking', companyId, 'sources'], queryFn: () => api.get<TrackingSourcesDto>(`/companies/${companyId}/tracking/sources`), enabled, staleTime: 5 * 60_000, retry: false })
}

export const domainsKey = (companyId: string) => ['landing-domains', companyId] as const

export function useLandingDomains(companyId: string) {
  return useQuery({
    queryKey: domainsKey(companyId),
    queryFn: () => api.get<LandingDomainsResponse>(`/companies/${companyId}/landing-domains`),
    // pendente: confere sozinho de tempos em tempos (a rotina do servidor verifica o DNS)
    refetchInterval: (q) => (q.state.data?.items.some((d) => d.status === 'pending') ? 30_000 : false),
  })
}

export function useDomainAction(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: { kind: 'create'; hostname: string; homePageId: string | null } | { kind: 'verify' | 'delete'; id: string } | { kind: 'home'; id: string; homePageId: string | null }): Promise<LandingDomainDto | null> => {
      const base = `/companies/${companyId}/landing-domains`
      switch (a.kind) {
        case 'create': return api.post<LandingDomainDto>(base, { hostname: a.hostname, homePageId: a.homePageId })
        case 'verify': return api.post<LandingDomainDto>(`${base}/${a.id}/verify`)
        case 'home': return api.patch<LandingDomainDto>(`${base}/${a.id}`, { homePageId: a.homePageId })
        case 'delete': return api.del<void>(`${base}/${a.id}`).then(() => null)
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: domainsKey(companyId) })
      void qc.invalidateQueries({ queryKey: landingKey(companyId) })
    },
  })
}
